import { useTranslation } from 'react-i18next'
import MilestoneProgressBar from './MilestoneProgressBar'
import { getPrizePhase, type Milestone } from './milestoneHelpers'
import { FaCheck } from 'react-icons/fa6'

import useContacts from '@/hooks/useContacts'
import { DEFAULT_CONTACT_GOAL, DISNEY_CONTACT_GOAL } from '@/util/defaults'

export default function PrizeProgressBar() {
  const { t } = useTranslation()
  const { contacts, contactGoal, goals } = useContacts()

  // `goals.contactGoal` is always populated once loaded: the transformer falls
  // back to the flat legacy contact goal when the API omits it.
  const contactGoalData = goals?.contactGoal
  const invitationCount = contactGoalData?.progress ?? contacts?.length ?? 0
  const participationGoal = contactGoalData?.target ?? contactGoal ?? DEFAULT_CONTACT_GOAL
  const rungs = contactGoalData?.goals

  const milestones: Milestone[] = rungs
    ? rungs.map((g: number, index: number): Milestone => {
        if (g === participationGoal) {
          return { position: g, variant: 'participation' }
        }

        if (index + 1 === rungs.length) {
          return { position: g, variant: 'disney', segmentGradient: ['#ffffff', 'var(--color-gold)'] }
        }

        return { position: g, variant: 'goal' }
      })
    : [
        { position: participationGoal, variant: 'participation' },
        { position: DISNEY_CONTACT_GOAL, variant: 'disney', segmentGradient: ['#ffffff', 'var(--color-gold)'] },
      ]

  //add the initial invitation milestone at the beginning of the milestones array, so it shows
  milestones.unshift({
    position: 0,
    variant: 'initial-invitation' as const,
    completeAt: 1,
  })

  const phase = getPrizePhase(invitationCount, participationGoal, DISNEY_CONTACT_GOAL)

  return (
    <div id='prize-progress-bar'>
      <MilestoneProgressBar
        title={t('content.components.prize_progress.title')}
        value={invitationCount}
        max={DISNEY_CONTACT_GOAL}
        milestones={milestones}
      />

      <div className='mt-8 md:mt-12 flex items-end justify-between gap-4'>
        <div className='flex items-baseline gap-2'>
          <span className='text-cta text-lg lg:text-2xl font-serif font-bold'>{invitationCount}</span>
          <span className='text-sm text-secondary-txt'>{t('content.components.prize_progress.invites_sent')}</span>
        </div>
        <div className='flex flex-row-reverse items-baseline gap-2 text-right'>
          {phase === 'qualified' ? (
            <div className='flex flex-row justify-end'>
              <FaCheck className='bg-transparent text-gold h-5 w-5 rounded-sm mt-0.5' />
              <div className='text-base lg:text-xl lg:font-extrabold text-gold ml-1'>
                {t('content.components.prize_progress.in_raffle')}
              </div>
            </div>
          ) : phase === 'disney' ? (
            <>
              <span className='text-sm text-secondary-txt'>
                {t('content.components.prize_progress.to_disney_raffle')}
              </span>
              <span className='text-gold text-2xl font-bold'>{DISNEY_CONTACT_GOAL - invitationCount}</span>
            </>
          ) : (
            <>
              <span className='text-sm font-semibold text-secondary-txt'>
                {t('content.components.prize_progress.to_go')}
              </span>
              <span className='text-cta text-2xl font-bold'>{participationGoal - invitationCount}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
